/**
 * Parse date string from API response (backend returns "Jan 1, 0022 12:00:00 AM" format)
 * without the JS 2-digit year relative parsing issue.
 *
 * JavaScript's new Date() treats "0022" as "2022" because years 0-99 are
 * treated as relative to 2000. This utility parses year as-is (4 digits).
 */
export class DateParseService {
  // Month name to index mapping
  private static readonly MONTH_MAP: Record<string, string> = {
    'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
    'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
    'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
  };

  /**
   * Parse a date string from API, preserving the exact year value.
   * Falls back to standard Date parsing if the format doesn't match.
   *
   * Supports formats:
   * - "Oct 20, 0010 12:00:00 AM" (backend Java format)
   * - "dd/MM/yyyy" (backend Vietnamese format)
   * - "yyyy-MM-dd'T'HH:mm:ss" (ISO-like)
   * - "yyyy-MM-dd" (simple date)
   * - standard Date parsable strings
   */
  static parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;

    // 1. Try parsing "MMM d, yyyy HH:mm:ss a" (backend Java format)
    // Extract components via regex — avoids JS 2-digit year relative parsing
    const jsFormat = dateStr.match(/^(\w+)\s+(\d+),\s+(\d{4})\s+(\d+):(\d+):(\d+)\s+(\w+)$/);
    if (jsFormat) {
      const [, month, day, year] = jsFormat;
      const paddedDay = day.padStart(2, '0');
      const paddedMonth = DateParseService.MONTH_MAP[month] || '01';
      // Create date using year as 4-digit string — but JS Date doesn't support year < 100
      // We need to construct it without the 2-digit year rule
      const y = parseInt(year, 10);
      // For years 0-99, use setFullYear to avoid the 2-digit rule
      const date = new Date(Date.UTC(2000, parseInt(paddedMonth, 10) - 1, parseInt(paddedDay, 10)));
      date.setUTCFullYear(y);
      return date;
    }

    // 2. Try parsing "dd/MM/yyyy" (backend Vietnamese format)
    const ddMmYyyy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddMmYyyy) {
      const [, day, month, year] = ddMmYyyy;
      const d = parseInt(day, 10);
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);
      if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1000) {
        const date = new Date(Date.UTC(2000, m - 1, d));
        date.setUTCFullYear(y);
        return date;
      }
    }

    // 3. Fallback to standard Date parsing
    const fallback = new Date(dateStr);
    if (!isNaN(fallback.getTime())) {
      return fallback;
    }

    return null;
  }

  /**
   * Parse date and return a formatted string.
   * Returns empty string if dateStr is null/undefined/invalid.
   */
  static parseAndFormat(dateStr: string, formatStr: string = 'DD/MM/YYYY'): string {
    if (!dateStr) return '';

    // 1. Java format: "Oct 20, 0010 12:00:00 AM" — extract components directly
    const jsFormat = dateStr.match(/^(\w+)\s+(\d+),\s+(\d{4})\s+(\d+):(\d+):(\d+)\s+(\w+)$/);
    if (jsFormat) {
      const [, month, day, year] = jsFormat;
      const paddedDay = day.padStart(2, '0');
      const paddedMonth = DateParseService.MONTH_MAP[month] || '01';
      if (formatStr === 'DD/MM/YYYY') return `${paddedDay}/${paddedMonth}/${year}`;
      if (formatStr === 'dd-MM-yyyy') return `${paddedDay}-${paddedMonth}-${year}`;
      return `${paddedDay}/${paddedMonth}/${year}`;
    }

    // 2. dd/MM/yyyy format — preserve exact year from DB (even year 0001, 0010, etc.)
    const ddMmYyyy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddMmYyyy) {
      const [, day, month, year] = ddMmYyyy;
      const d = parseInt(day, 10);
      const m = parseInt(month, 10);
      if (d >= 1 && d <= 31 && m >= 1 && m <= 12) {
        const paddedDay = String(d).padStart(2, '0');
        const paddedMonth = String(m).padStart(2, '0');
        if (formatStr === 'DD/MM/YYYY') return `${paddedDay}/${paddedMonth}/${year}`;
        if (formatStr === 'dd-MM-yyyy') return `${paddedDay}-${paddedMonth}-${year}`;
        return `${paddedDay}/${paddedMonth}/${year}`;
      }
    }

    // 3. Parse other formats via Date
    const date = this.parseDate(dateStr);
    if (!date) return '';

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = String(date.getUTCFullYear()).padStart(4, '0');

    if (formatStr === 'DD/MM/YYYY') return `${day}/${month}/${year}`;
    if (formatStr === 'dd-MM-yyyy') return `${day}-${month}-${year}`;
    return `${day}/${month}/${year}`;
  }
}